import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { HttpBackend } from '@angular/common/http';
import { provideRouter, withRouterConfig } from '@angular/router';

import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideTranslateService, TranslateCompiler, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';

import { APP_CONFIG } from '@kros-sk/core/app-config';
import { loadAppVersion } from '@kros-sk/core/app-version';
import { provideAppInsights } from '@kros-sk/core/application-insights';
import { provideFeatureFlags } from '@kros-sk/core/feature-flags';
import { HttpLoaderFactory } from '@kros-sk/core/i18n-loader';
import { ApplicationType, getDefaultTranslationCulture } from '@kros-sk/models';
import {
  createInterceptedAuthUrlFilter,
  LOAD_FEATURE_FLAGS_IN_AUTH,
  localeInterceptorFn,
  provideApiConfigAndAuth,
  provideInterceptedHttpClient,
} from '@kros-sk/auth';
import { HttpErrorHandlingInterceptorFn } from '@kros-sk/error-handling';
import { httpMockInterceptor } from '@kros-sk/core/http-mock';
import { provideDeviceDetector } from '@kros-sk/device-detector';
import { provideLocalizationFormatters } from '@kros-sk/shared/localization';
import {
  AuthProbeService,
  DocumentHashRedactionPlugin,
  trackVisitedPath,
} from '@kros-sk/document-viewer/public-document-view';
import { provideModuleRouter } from '@kros-sk/router-stores/module-router-store';
import { providePdfjsSelfHostedAssets } from '@kros-sk/ui/schema-pdf-viewer/assets';
import { provideSplashScreen } from '@kros-sk/ui/splash-screen';
import { provideTheme } from '@kros-sk/ui/theming';

import { environment } from '../environments/environment';
import { routes } from './app.routes';

const identityServerConfig = {
  clientUrl: environment.identityServer.clientUrl,
  url: environment.identityServer.url,
  postLogoutUrl: `${environment.identityServer.clientUrl}/oidc/logout-redirect`,
  optionalAuthentication: true,
};

function initializeApp(): void {
  loadAppVersion();
  const translate = inject(TranslateService);
  translate.setDefaultLang(getDefaultTranslationCulture());
  translate.use(getDefaultTranslationCulture());
}

function probeSilentSsoSession(): void {
  inject(AuthProbeService).run();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    { provide: LOCALE_ID, useValue: 'sk' },
    { provide: APP_CONFIG, useValue: environment },

    provideLocalizationFormatters(),
    provideTheme({}),
    provideTranslateService({
      defaultLanguage: getDefaultTranslationCulture(),
      loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpBackend] },
      compiler: { provide: TranslateCompiler, useClass: TranslateMessageFormatCompiler },
    }),
    provideAppInitializer(() => initializeApp()),

    provideStore(),
    provideEffects([]),
    provideModuleRouter(),
    provideDeviceDetector(),
    provideAppInsights(() => ({
      instrumentationKey: environment.appInsightsInstrumentationKey,
      aiCloudRole: 'Kros.Esw.FE.DocumentViewer',
      samplingPercentage: environment.appInsightsSamplingPercentage,
      customConfig: {
        enableAutoRouteTracking: false,
        extensions: [new DocumentHashRedactionPlugin()],
      },
    })),
    provideFeatureFlags(),
    provideSplashScreen(),
    providePdfjsSelfHostedAssets(),

    provideApiConfigAndAuth(
      identityServerConfig,
      'Kros.Frontend',
      'openid profile email Esw.Invoicing',
      ApplicationType.BusinessApp
    ),
    provideAppInitializer(probeSilentSsoSession),
    provideAppInitializer(trackVisitedPath),
    { provide: LOAD_FEATURE_FLAGS_IN_AUTH, useValue: false },
    provideInterceptedHttpClient(
      createInterceptedAuthUrlFilter(environment.gatewayApiUrl, environment.gatewayApiUrl, [
        `${environment.gatewayApiUrl}/documentLink`,
        `${environment.gatewayApiUrl}/purchaserSegment`,
        `${environment.gatewayApiUrl}/einvoiceXml`,
      ]),
      [
        httpMockInterceptor,
        HttpErrorHandlingInterceptorFn,
        localeInterceptorFn((url: string) => !url.includes('/assets/i18n/') && url.includes(`${environment.gatewayApiUrl}`)),
      ]
    ),
  ],
};
